import React, { useState } from 'react';
import styles from '../AssignedJobsSection.module.css';
import { fmtHrs, fmtUSD, paymentState } from './jobsApi';
import { RobotCard } from './JobRowCells';

// ── Job form modal (create / edit) ──────────────────────────────────────────

const JobFormModal = ({ job, orders, adminUserId, onCancel, onSaved, onMarkPaid, onRequestUnmark, getHeaders, jobsBaseUrl }) => {
    const isEdit = !!job;
    const [taskRows, setTaskRows] = useState(
        isEdit && job.tasks.length
            ? job.tasks.map((t) => ({ task_id: t.task_id, assigned_hours: String(t.assigned_hours) }))
            : [{ task_id: '', assigned_hours: '' }]
    );
    const [rate, setRate] = useState(isEdit ? String(job.rate_usd_per_hour.toFixed(2)) : '18.50');
    const [dueDate, setDueDate] = useState(() => {
        if (isEdit) return job.due_date;
        const d = new Date();
        d.setUTCDate(d.getUTCDate() + 14);
        return d.toISOString().slice(0, 10);
    });
    const [orderId, setOrderId] = useState(isEdit && job.order_id ? String(job.order_id) : '');
    const [orderErr, setOrderErr] = useState('');
    const [tasksErr, setTasksErr] = useState('');
    const [rateErr, setRateErr] = useState('');
    const [dueErr, setDueErr] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveErr, setSaveErr] = useState('');
    const [markingPaid, setMarkingPaid] = useState(false);
    const [payErr, setPayErr] = useState('');

    // The job's own order may no longer be in the available-orders list (e.g.
    // it has since moved past Production/Delivering) — fall back to what the
    // job already carries so an existing selection is never silently dropped.
    const selectedOrder = orders.find((o) => String(o.order_id) === orderId)
        || (isEdit && job.order_id && String(job.order_id) === orderId
            ? { order_id: job.order_id, robot: job.robot || null, tasks: job.tasks.map((t) => ({ task_id: t.task_id, name: t.task_name })) }
            : null);
    const orderTasks = selectedOrder ? selectedOrder.tasks : [];

    const takenTaskIds = taskRows.map((r) => r.task_id).filter(Boolean);
    const hoursTotal = taskRows.reduce((a, r) => a + (parseFloat(r.assigned_hours) || 0), 0);
    const namedCount = taskRows.filter((r) => r.task_id).length;
    const taskPoolExhausted = !orderId || taskRows.length >= orderTasks.length;

    const updateRow = (i, patch) => {
        setTaskRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
        setTasksErr('');
    };
    const addRow = () => {
        if (taskPoolExhausted) return;
        setTaskRows((rows) => [...rows, { task_id: '', assigned_hours: '' }]);
    };
    const removeRow = (i) => {
        setTaskRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, idx) => idx !== i)));
    };

    // Switching orders re-scopes which tasks are selectable — rows the new
    // order doesn't cover can't survive, so they're dropped with a hint
    // rather than left pointing at a task no longer in scope.
    const handleOrderChange = (nextOrderId) => {
        setOrderId(nextOrderId);
        setOrderErr('');
        const pool = (orders.find((o) => String(o.order_id) === nextOrderId) || { tasks: [] }).tasks;
        const poolIds = pool.map((t) => String(t.task_id));
        setTaskRows((rows) => {
            const kept = rows.filter((r) => !r.task_id || poolIds.includes(r.task_id));
            return kept.length ? kept : [{ task_id: '', assigned_hours: '' }];
        });
    };

    const pay = isEdit ? paymentState(job) : { key: 'none' };
    const loggedHours = isEdit ? Number(job.hours_logged || 0) : 0;
    const earnedPreview = Math.round(loggedHours * (parseFloat(rate) || 0) * 100) / 100;
    const notPaidActive = !isEdit || pay.key !== 'paid';
    const paidActive = isEdit && pay.key === 'paid';
    const canMarkPaid = isEdit && pay.key !== 'none' && pay.key !== 'paid';
    const canUnmark = isEdit && pay.key === 'paid';
    const payHintText = !isEdit
        ? 'New jobs start unpaid.'
        : pay.key === 'paid'
            ? `Keeps the ${fmtUSD(job.amount_paid_total)} already on record. Anything earned since shows as outstanding.`
            : pay.key === 'pending_review'
                ? `${fmtUSD(job.amount_outstanding)} hasn't cleared customer review yet, but you can still mark it paid now if needed.`
                : `${fmtUSD(job.amount_outstanding)} is ready to pay now that the order has cleared customer approval.`;

    const handleMarkPaidClick = async () => {
        if (!canMarkPaid) return;
        setMarkingPaid(true);
        setPayErr('');
        try {
            await onMarkPaid(job);
        } catch (e) {
            setPayErr(e.message || 'Failed to record payment.');
        } finally {
            setMarkingPaid(false);
        }
    };

    const handleSave = async () => {
        setOrderErr(''); setTasksErr(''); setRateErr(''); setDueErr(''); setSaveErr('');

        if (!orderId) {
            setOrderErr('Pick the order this job belongs to.');
            return;
        }

        const used = taskRows.filter((r) => r.task_id || String(r.assigned_hours).trim() !== '');
        const bad = used.some((r) => !r.task_id || !(parseFloat(r.assigned_hours) > 0));
        if (used.length === 0 || bad) {
            setTasksErr('Pick a task and give it hours above zero.');
            return;
        }
        const rateNum = parseFloat(rate);
        if (!(rateNum >= 0)) {
            setRateErr('Enter a rate of zero or more.');
            return;
        }
        const assignedDateStr = isEdit ? job.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10);
        if (!dueDate || dueDate < assignedDateStr) {
            setDueErr('Due date falls before the assigned date.');
            return;
        }

        const payload = {
            tasks: used.map((r) => ({ task_id: Number(r.task_id), assigned_hours: parseFloat(r.assigned_hours) })),
            rate_usd_per_hour: rateNum,
            due_date: dueDate,
            order_id: Number(orderId),
        };

        setSaving(true);
        try {
            let res;
            if (isEdit) {
                res = await fetch(`${jobsBaseUrl}/${job.job_id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...getHeaders() },
                    body: JSON.stringify(payload),
                });
            } else {
                res = await fetch(jobsBaseUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getHeaders() },
                    body: JSON.stringify({ ...payload, user_id: adminUserId }),
                });
            }
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.msg || 'Failed to save job');
            onSaved(json.data);
        } catch (e) {
            setSaveErr(e.message || 'Failed to save job.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.moOverlay}>
            <div className={styles.moDim} onClick={onCancel} />
            <div className={styles.modal}>
                <div className={styles.modalHdr}>
                    <span className={styles.modalTitle}>{isEdit ? `Edit ${job.job_id}` : 'New job'}</span>
                    <button className={styles.dClose} onClick={onCancel}>×</button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.roPanel}>
                        <div className={styles.roHdr}>Set by the platform — read-only</div>
                        <div className={styles.roGrid}>
                            <div className={styles.roItem}>
                                <div className={styles.roLbl}>Job ID</div>
                                <div className={`${styles.roVal} ${!isEdit ? styles.roValPending : ''}`}>
                                    {isEdit ? job.job_id : 'Issued on save'}
                                </div>
                            </div>
                            <div className={styles.roItem}>
                                <div className={styles.roLbl}>Assigned date</div>
                                <div className={`${styles.roVal} ${!isEdit ? styles.roValPending : ''}`}>
                                    {isEdit ? job.created_at.slice(0, 10) : 'Set on save'}
                                </div>
                            </div>
                            <div className={styles.roItem}>
                                <div className={styles.roLbl}>Hours logged</div>
                                <div className={styles.roVal}>{fmtHrs(loggedHours)} h</div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.fieldLbl}>Order</label>
                        <select
                            className={`${styles.inp} ${styles.sel}`}
                            value={orderId}
                            onChange={(e) => handleOrderChange(e.target.value)}
                        >
                            <option value="">Select an order…</option>
                            {orders.map((o) => {
                                const topTask = o.tasks.length
                                    ? o.tasks.reduce((a, t) => (t.hours > a.hours ? t : a))
                                    : null;
                                return (
                                    <option key={o.order_id} value={o.order_id}>
                                        ORD-{o.order_id}{o.robot ? ` · ${o.robot.product_name}` : ''}{topTask ? ` · ${o.customer_name || '—'} - ${topTask.name} - ${fmtHrs(topTask.hours)}h` : ''}
                                    </option>
                                );
                            })}
                            {selectedOrder && !orders.some((o) => o.order_id === selectedOrder.order_id) && (
                                <option value={selectedOrder.order_id}>ORD-{selectedOrder.order_id} (current)</option>
                            )}
                        </select>
                        {orderErr && <div className={styles.errMsg}>{orderErr}</div>}
                        <div className={styles.fieldHint}>Tasks are drawn from the order you pick.</div>
                        <RobotCard robot={selectedOrder ? selectedOrder.robot : null} hasOrder={!!orderId} />
                    </div>

                    <div className={styles.field}>
                        <div className={styles.taskHdr}>
                            <label className={styles.fieldLbl}>Tasks</label>
                            <span className={styles.taskTotal}>{fmtHrs(hoursTotal)} h assigned · {namedCount} task{namedCount === 1 ? '' : 's'}</span>
                        </div>
                        <div className={styles.taskRows}>
                            {taskRows.map((row, i) => (
                                <div className={styles.taskRow} key={i}>
                                    <select
                                        className={`${styles.inp} ${styles.taskSel}`}
                                        value={row.task_id}
                                        disabled={!orderId}
                                        onChange={(e) => updateRow(i, { task_id: e.target.value })}
                                    >
                                        <option value="">{orderId ? 'Select a task…' : 'Select an order first'}</option>
                                        {orderTasks.map((t) => {
                                            const usedElsewhere = takenTaskIds.includes(String(t.task_id)) && String(t.task_id) !== row.task_id;
                                            return (
                                                <option key={t.task_id} value={t.task_id} disabled={usedElsewhere}>
                                                    {t.name}{usedElsewhere ? ' — already added' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <input
                                        className={`${styles.inp} ${styles.taskHrs}`}
                                        type="number" min="0.25" step="0.25" placeholder="0.00"
                                        value={row.assigned_hours}
                                        onChange={(e) => updateRow(i, { assigned_hours: e.target.value })}
                                    />
                                    <button
                                        className={styles.tDel}
                                        title="Remove task"
                                        disabled={taskRows.length <= 1}
                                        onClick={() => removeRow(i)}
                                    >×</button>
                                </div>
                            ))}
                        </div>
                        <button className={styles.btnAddTask} disabled={taskPoolExhausted} onClick={addRow}>+ Add task</button>
                        {tasksErr && <div className={styles.errMsg}>{tasksErr}</div>}
                        <div className={styles.fieldHint}>Tasks are drawn from the order you pick. Hours assigned is the sum of these rows.</div>
                    </div>

                    <div className={styles.fieldRow}>
                        <div className={styles.field}>
                            <label className={styles.fieldLbl}>Rate (USD/h)</label>
                            <input className={styles.inp} type="number" min="0" step="0.01" placeholder="18.50"
                                value={rate} onChange={(e) => { setRate(e.target.value); setRateErr(''); }} />
                            {rateErr && <div className={styles.errMsg}>{rateErr}</div>}
                        </div>
                        <div className={styles.field}>
                            <label className={styles.fieldLbl}>Due date</label>
                            <input className={styles.inp} type="date" value={dueDate || ''}
                                onChange={(e) => { setDueDate(e.target.value); setDueErr(''); }} />
                            {dueErr && <div className={styles.errMsg}>{dueErr}</div>}
                        </div>
                    </div>

                    <div className={styles.field} style={{ marginTop: '-8px' }}>
                        <div className={styles.fieldHint}>
                            Earned is hours logged × rate — <span style={{ color: '#aaa' }}>{fmtUSD(earnedPreview)}</span>.
                            Logged hours come from session telemetry, so this moves on its own as the operator works.
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLbl}>Payment status</label>
                        <div className={styles.seg}>
                            <button
                                type="button"
                                className={`${styles.segBtn} ${notPaidActive ? styles.segBtnOn : ''}`}
                                disabled={!canUnmark}
                                title={canUnmark ? 'Choose which payments to mark unpaid' : undefined}
                                onClick={() => onRequestUnmark(job)}
                            >Not paid</button>
                            <button
                                type="button"
                                className={`${styles.segBtn} ${paidActive ? `${styles.segBtnOn} ${styles.segBtnPayOn}` : ''}`}
                                disabled={!canMarkPaid || markingPaid}
                                onClick={handleMarkPaidClick}
                            >{markingPaid ? 'Marking…' : 'Paid'}</button>
                        </div>
                        <div className={styles.fieldHint}>{payHintText}</div>
                        {payErr && <div className={styles.errMsg} style={{ display: 'block' }}>{payErr}</div>}
                    </div>

                    {saveErr && <div className={styles.errMsg} style={{ display: 'block' }}>{saveErr}</div>}
                </div>
                <div className={styles.modalFtr}>
                    <button className={styles.btnGhost} onClick={onCancel} disabled={saving}>Cancel</button>
                    <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create job')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobFormModal;
